import { useEffect, useMemo, useState } from "react";
import { BRANCHES, CATEGORIES } from "./data";
import { getActualValuation } from "./pilotApi";
import { SAMPLE_AS_OF, fmtDate } from "./origin";

// -----------------------------------------------------------------------------
// Ending Inventory valuation at historical cost — one shared figure for
// Inventory Intelligence, the Command Center KPI and the Morning Brief snapshot.
// Branches with an ingested 1Rotary snapshot use Actual cells from the backend;
// every other branch is filled from the synthetic sample.
// -----------------------------------------------------------------------------
const CAT_WEIGHTS = { AUTO: 0.34, HVAC: 0.31, REFR: 0.24, CONS: 0.11 };
const AVG_UNIT_COST = { AUTO: 6200, HVAC: 4800, REFR: 3900, CONS: 950 };
const CAT_NAME = Object.fromEntries(CATEGORIES.map(c => [c.id, c.name]));
const STALE_DAYS = 7;
export const SYNTHETIC_VALUATION_SOURCE = "Synthetic sample dataset · branch inventory snapshot × sample unit cost";

const SYNTHETIC_CELLS = BRANCHES.flatMap((b, i) => {
  const ids = Object.keys(CAT_WEIGHTS);
  const raw = ids.map((id, k) => CAT_WEIGHTS[id] * (0.85 + ((i * 7 + k * 13) % 30) / 100));
  const sum = raw.reduce((s, x) => s + x, 0);
  let allocated = 0;
  return ids.map((id, k) => {
    const value = k === ids.length - 1 ? b.inventoryValue - allocated : Math.round((b.inventoryValue * raw[k]) / sum);
    allocated += value;
    return {
      branch_id: b.id, category: CAT_NAME[id], value,
      items: Math.max(1, Math.round((b.skuCount * raw[k]) / sum)),
      units: Math.round(value / AVG_UNIT_COST[id]),
      excluded: (i + k) % 9 === 0 ? 1 + ((i + k) % 3) : 0,
      origin: "synthetic",
    };
  });
});

let _promise = null;
let _at = 0;
function fetchActual() {
  if (!_promise || Date.now() - _at > 60_000) {
    _at = Date.now();
    _promise = getActualValuation().catch(() => ({ available: false, reason: "Valuation service unreachable." }));
  }
  return _promise;
}

function group(cells, key, extra) {
  const m = new Map();
  cells.forEach(c => {
    const k = c[key];
    const g = m.get(k) || { key: k, value: 0, items: 0, units: 0, excluded: 0, origins: new Set(), ...extra(c) };
    g.value += c.value; g.items += c.items; g.units += c.units; g.excluded += c.excluded;
    g.origins.add(c.origin);
    m.set(k, g);
  });
  return [...m.values()];
}

export function buildValuation(actual, branchId) {
  const byId = new Map(BRANCHES.map(b => [b.id, b]));
  const scopeIds = new Set(branchId ? [branchId] : BRANCHES.map(b => b.id));
  const act = actual?.available
    ? actual.cells.filter(c => !branchId || c.branch_id === branchId).map(c => ({ ...c, origin: "actual" }))
    : [];
  const actBranches = new Set(act.map(c => c.branch_id));
  const syn = SYNTHETIC_CELLS.filter(c => scopeIds.has(c.branch_id) && !actBranches.has(c.branch_id));
  const synBranches = new Set(syn.map(c => c.branch_id));
  const cells = [...act, ...syn];
  const origin = act.length && syn.length ? "mixed" : act.length ? "actual" : "synthetic";

  const total = cells.reduce((s, c) => s + c.value, 0);
  const pick = (g) => ({ ...g, share: total ? g.value / total : 0, origin: g.origins.size > 1 ? "mixed" : [...g.origins][0] });
  const byBranch = group(cells, "branch_id", c => ({ name: byId.get(c.branch_id)?.name || c.branch_id, region: byId.get(c.branch_id)?.region || "—" }))
    .map(pick).sort((a, b) => b.value - a.value);
  const byCategory = group(cells, "category", () => ({})).map(g => ({ ...pick(g), name: g.key })).sort((a, b) => b.value - a.value);

  const actAsOf = [...actBranches].map(b => actual.branch_as_of?.[b]).filter(Boolean).sort().pop() || actual?.as_of;
  const asOf = act.length ? actAsOf : SAMPLE_AS_OF;
  const ageDays = Math.floor((Date.now() - new Date(`${asOf}T00:00:00`).getTime()) / 86_400_000);
  const actualSource = actual?.source || "1Rotary S3 inventory snapshot";
  const source = origin === "synthetic" ? SYNTHETIC_VALUATION_SOURCE
    : origin === "actual" ? actualSource : `${actualSource} + synthetic sample for remaining branches`;
  const parts = origin === "mixed" ? [
    { origin: "actual", text: `${actBranches.size} branch${actBranches.size === 1 ? "" : "es"} from ${actualSource}, as of ${fmtDate(actAsOf)}` },
    { origin: "synthetic", text: `${synBranches.size} branches from the synthetic sample dataset` },
  ] : [];

  return {
    loaded: actual !== null,
    total, origin, source, parts, asOf, ageDays, stale: ageDays > STALE_DAYS,
    itemsValued: cells.reduce((s, c) => s + c.items, 0),
    unitsValued: cells.reduce((s, c) => s + c.units, 0),
    excluded: cells.reduce((s, c) => s + c.excluded, 0),
    byBranch, byCategory,
    valueByBranch: Object.fromEntries(byBranch.map(b => [b.key, b.value])),
    actualReason: actual && !actual.available ? actual.reason : null,
    originProps: { origin, source, updatedAt: asOf, parts },
  };
}

export function useInventoryValuation(branchId) {
  const [actual, setActual] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchActual().then(a => alive && setActual(a));
    return () => { alive = false; };
  }, []);
  return useMemo(() => buildValuation(actual, branchId || null), [actual, branchId]);
}
